import { useEffect, useState } from "react";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import { Alert, Button, Spinner } from "react-bootstrap";
import FormInput from "../../components/FormInput";
import errorInfoIcon from "../../assets/icons/error_info.svg";
import backBtn from "../../assets/icons/arrow_left.svg";

interface Permissions {
	[category: string]: string[];
}

interface RoleDetails {
	role: string;
	permissions: { [category: string]: string[] };
}

interface UpdateRoleProps {
	roleId: string;
	onHide: () => void;
}

interface FlattenedPermissions {
	[category: string]: string[];
}

const UpdateRole = ({ roleId, onHide }: UpdateRoleProps) => {

	const [permissionsList, setPermissionsList] = useState<Permissions[]>([]);
	const [roleDetails, setRoleDetails] = useState<RoleDetails | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showAlert, setShowAlert] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [formData, setFormData] = useState({
		role: "",
		permissions: {} as { [key: string]: string[] },
	});

	const capitalizeFirstLetter = (string: string) => {
		if (!string) return '';
		return string.charAt(0).toUpperCase() + string.slice(1);
	};

	const flattenPermissionsForUI = (permissions: { [key: string]: string[] }[]): FlattenedPermissions => {
		const flattenedPermissions: FlattenedPermissions = {};
		permissions.forEach(permissionObj => {
			Object.keys(permissionObj).forEach(category => {
				if (!flattenedPermissions[category]) {
					flattenedPermissions[category] = [];
				}
				flattenedPermissions[category].push(...permissionObj[category]);
			});
		});
		return flattenedPermissions;
	};

	useEffect(() => {
		const fetchPermissionsList = async () => {
			setIsLoading(true);
			try {
				api.setAuthorization(api.getLoggedInUser());
				const response = await api.get(endpoints.getPermissionsList, {})
				setPermissionsList(response.data.data);
				setIsLoading(false);
			} catch (error: any) {
				console.error("Error fetching permissions:", error);
				setError("Failed to fetch permissions.");
				setShowAlert(true);
			} finally {
				setIsLoading(false);
			}
		};

		const fetchRoleDetails = async () => {
			setIsLoading(true);
			try {
				const roleResponse = await api.get(endpoints.getRole(roleId), {});
				if (roleResponse.data.data.role && roleResponse.data.data.permissions) {
					const transformedPermissions = flattenPermissionsForUI(roleResponse.data.data.permissions);
					setRoleDetails({
						role: roleResponse.data.data.role,
						permissions: transformedPermissions,
					});
					setFormData({
						role: roleResponse.data.data.role,
						permissions: transformedPermissions,
					});
				}
			} catch (error) {
				console.error("Error fetching role details:", error);
				setError("Failed to fetch role details.");
				setShowAlert(true);
			} finally {
				setIsLoading(false);
			}
		};

		fetchPermissionsList();
		fetchRoleDetails();
		setShowAlert(false);
	}, [roleId]);


	const handleCheckboxChange = (category: string, permission: string, isChecked: boolean) => {
		setFormData(prevFormData => {
			const updatedPermissions = { ...prevFormData.permissions };
			if (isChecked) {
				if (!updatedPermissions[category]?.includes(permission)) {
					updatedPermissions[category] = [...(updatedPermissions[category] || []), permission];
				}
			} else {
				updatedPermissions[category] = updatedPermissions[category]?.filter(p => p !== permission) || [];
			}
			return { ...prevFormData, permissions: updatedPermissions };
		});
	};

	const prepareUpdatePayload = () => {
		const permissionsArray = Object.entries(formData.permissions).map(([category, permissions]) => {
			return { [category]: permissions };
		});
		return {
			role: formData.role,
			permissions: permissionsArray,
		};
	};

	const handleUpdateRole = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsSubmitting(true);
		const updatePayload = prepareUpdatePayload();
		try {
			api.setAuthorization(api.getLoggedInUser());
			const response = await api.update(endpoints.updateRole(roleId), updatePayload);
			setSuccessMessage("Role updated successfully.");
			setShowAlert(true);
		} catch (error: any) {
			const message = error?.response?.data?.message || 'Unable to update role please try again';
			setError(message);
			setShowAlert(true);
			setSuccessMessage(null);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<div className="modal-backdrop" onClick={onHide}></div>
			<form onSubmit={handleUpdateRole}>
				<div className="role-update-form">
					<div style={{display:'flex', alignItems:'center'}}>
						<img src={backBtn} alt="back" onClick={onHide} style={{ cursor: 'pointer' }} />
						<h3>Edit Role</h3>
					</div>
					<div className=" detail-section role-create-section">
						<h4>Role Title</h4>
						<FormInput
							aria-label="Role Title"
							name="role"
							value={formData.role}
							onChange={(e) => setFormData({ ...formData, role: e.target.value })}
						/>
					</div>

					<div className="detail-section role-create-section">
						<div>
							<h4>Permissions</h4>
							<p>Assign certain permissions to a role based on admin</p>
						</div>

						{isLoading ? (
							<Spinner animation="border" size="sm" role="status" />
						) : (

							<div className="permissions-container">
								{permissionsList.map((permissionObj, index) => {
									const category = Object.keys(permissionObj)[0];
									return (
										<div key={index} className="permissions-category">
											<h5>{capitalizeFirstLetter(category)}</h5>
											{permissionObj[category].map(permission => (
												<div key={permission} className="permissions-list">
													<input
														type="checkbox"
														id={`${category}-${permission}`}
														name={`${category}-${permission}`}
														checked={formData.permissions[category]?.includes(permission) || false}
														onChange={e => handleCheckboxChange(category, permission, e.target.checked)}
													/>
													<label htmlFor={`${category}-${permission}`}>{permission}</label>
												</div>
											))}
										</div>
									);
								})}
							</div>
						)}
					</div>

					{showAlert && (
						<Alert
							variant={error ? 'danger' : 'success'}
							className={error ? 'alert-error' : 'alert-success'}
						>
							{error && <img src={errorInfoIcon} alt="error" />}
							{error ? error : successMessage}
						</Alert>
					)}

					<Button
						className="text-center"
						type="submit"
						disabled={isSubmitting}
					>
						{isSubmitting ? <Spinner animation="border" size="sm" /> : "Update Role"}
					</Button>
				</div>
			</form>
		</>
	)
};

export default UpdateRole;